import styled, { css } from "styled-components";
import { BORDER_COLORS, COLORS, LAYER_COLORS, TEXT_COLORS } from "../../../constants/design/colors";
import { FONT_CSS } from "../../../constants/design/fontSize";
import { SPACING } from "../../../constants/design/spacing";

// Account, status, collateral, uPnL, maintenance margin, positions, collateral assets
const MEMBER_COLUMNS = "minmax(220px, 1.6fr) 70px repeat(3, minmax(96px, 0.8fr)) 70px minmax(160px, 1.4fr)";
const POSITION_COLUMNS = "minmax(160px, 1.6fr) 50px repeat(5, minmax(80px, 1fr)) 60px";
const MIN_TABLE_WIDTH = 940;

export const FamilyWrapper = styled.div`
  margin: ${SPACING.two}px;
  border: 1px solid ${BORDER_COLORS.one};
  border-radius: 8px;
  background: ${LAYER_COLORS.one};
  overflow-x: auto;
`;

export const FamilyHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${SPACING.two}px;
  padding: ${SPACING.twoHalf}px ${SPACING.three}px;
  border-bottom: 1px solid ${BORDER_COLORS.one};

  > span {
    ${FONT_CSS.label.two};
    color: ${TEXT_COLORS.one};
    text-transform: uppercase;
  }
`;

export const TextButton = styled.button.attrs({ type: "button" })`
  ${FONT_CSS.label.two};
  color: ${COLORS.blue.one};
  background: none;
  border: none;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

const row = css`
  ${FONT_CSS.label.two};
  display: grid;
  grid-template-columns: ${MEMBER_COLUMNS};
  align-items: center;
  gap: ${SPACING.two}px;
  padding: ${SPACING.two}px ${SPACING.three}px;
  min-width: ${MIN_TABLE_WIDTH}px;
  box-sizing: border-box;
  text-transform: none;

  > *:not(:first-child, :nth-child(2), :last-child) {
    text-align: right;
  }

  > *:last-child {
    padding-left: ${SPACING.three}px;
  }
`;

export const MemberHeaderRow = styled.div`
  ${row};
  color: ${TEXT_COLORS.three};
  text-transform: uppercase;
  border-bottom: 1px solid ${BORDER_COLORS.two};
`;

export const TotalsRow = styled.div`
  ${row};
  color: ${TEXT_COLORS.one};
  background: ${COLORS.white.five};
  border-bottom: 1px solid ${BORDER_COLORS.one};
`;

export const MemberRow = styled.div<{ isCurrent?: boolean; isArchived?: boolean }>`
  ${row};
  color: ${TEXT_COLORS.one};
  border-top: 1px solid ${BORDER_COLORS.two};
  cursor: pointer;
  opacity: ${({ isArchived }) => (isArchived ? 0.55 : 1)};
  box-shadow: ${({ isCurrent }) => (isCurrent ? `inset 3px 0 0 ${COLORS.blue.one}` : "none")};
  background: ${({ isCurrent }) => (isCurrent ? COLORS.blue.five : "transparent")};

  &:hover {
    background: ${COLORS.white.five};
  }
`;

export const MemberCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.two}px;
  min-width: 0;
`;

export const Caret = styled.span<{ isOpen: boolean }>`
  width: 12px;
  flex-shrink: 0;
  color: ${TEXT_COLORS.three};
  transform: rotate(${({ isOpen }) => (isOpen ? 90 : 0)}deg);
  transition: transform 0.15s ease;
`;

export const MemberName = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  > a {
    color: ${TEXT_COLORS.one};
    text-decoration: none;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:hover {
      color: ${COLORS.blue.one};
    }
  }

  > span {
    color: ${TEXT_COLORS.three};
    font-family: monospace;
  }
`;

export const RoleTag = styled.span<{ variant: "master" | "sub" | "current" }>`
  ${FONT_CSS.label.three};
  flex-shrink: 0;
  padding: 2px ${SPACING.one}px;
  border-radius: 4px;
  text-transform: uppercase;
  ${({ variant }) => {
    if (variant === "master")
      return css`
        color: ${COLORS.blue.one};
        background: ${COLORS.blue.two};
      `;
    if (variant === "sub")
      return css`
        color: ${COLORS.orange.one};
        background: ${COLORS.orange.five};
      `;
    return css`
      color: ${TEXT_COLORS.one};
      background: ${COLORS.white.three};
    `;
  }}
`;

export const StatusText = styled.span<{ isArchived?: boolean }>`
  color: ${({ isArchived }) => (isArchived ? TEXT_COLORS.three : COLORS.green.one)};
`;

export const AssetsText = styled.span`
  color: ${TEXT_COLORS.two};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const PositionsTable = styled.div`
  padding: 0 ${SPACING.three}px ${SPACING.twoHalf}px ${SPACING.six}px;
  min-width: ${MIN_TABLE_WIDTH}px;
  box-sizing: border-box;
`;

export const PositionRow = styled.div<{ isHeader?: boolean }>`
  ${FONT_CSS.label.two};
  display: grid;
  grid-template-columns: ${POSITION_COLUMNS};
  gap: ${SPACING.two}px;
  padding: ${SPACING.one}px ${SPACING.two}px;
  color: ${({ isHeader }) => (isHeader ? TEXT_COLORS.three : TEXT_COLORS.two)};
  text-transform: ${({ isHeader }) => (isHeader ? "uppercase" : "none")};
  border-left: 1px solid ${BORDER_COLORS.one};

  > *:not(:first-child, :nth-child(2)) {
    text-align: right;
  }
`;

export const EmptyText = styled.div`
  ${FONT_CSS.label.two};
  padding: ${SPACING.one}px ${SPACING.two}px;
  color: ${TEXT_COLORS.three};
  border-left: 1px solid ${BORDER_COLORS.one};
`;
